import { FC, Fragment } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Space,
	Table,
	Typography,
} from 'antd';
import { FileSearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import DataRow from '@/libs/antd/components/DataRow';
import {
	SubmitCCDHApprovalSurveyAnalysisPayload,
	getSurveyAnalysisDetail,
	submitCCDHApprovalSurveyAnalysis,
} from '@/libs/fleet-api/services/survey';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import TextArea from 'antd/es/input/TextArea';
import rule from '@/libs/antd/rules';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { formatCurrency } from '@/libs/antd/formater';
import { useSelectRejectCategory } from '@/libs/fleet-api/hooks/master-data';
import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import { DocumentItem } from '@/components/modals/DocumentModal';
import dayjs from 'dayjs';
import fetcher, { FleetFile } from '@/libs/fleet-api/fetcher';
import GenericErrorMessage from '@/components/GenericErrorMessage';

type CCDHSurveyApprovalPageProps = {
	prospectId: string;
};

const CCDHASurveyApprovalPage: FC<CCDHSurveyApprovalPageProps> = ({
	prospectId = '',
}) => {
	const { navigate, notification } = useApp();
	const { form, value, fieldName } =
		useForm<SubmitCCDHApprovalSurveyAnalysisPayload>();

	const { data, isLoading, isError } = useQuery({
		queryKey: ['getSurveyAnalysisDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getSurveyAnalysisDetail(id);
		},
	});

	const {
		mainInformation,
		customerLegalDocuments,
		customerBusinessDocuments,
		customerReferralDocuments,
		employmentContracts,
		prospectFinancialReports,
		prospectInformation,
		prospectBankStatements,
		approvalHistories,
		prospectFinancialReportAnalysis,
		surveyInformation,
	} = data?.data ?? {};

	const surveyParticipants = surveyInformation?.surveyParticipants;

	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openCustomerCheckingModal,
		openProspectDetail,
		openDocumentModal,
		openPreviewModal,
		openEmploymentContractModal,
		openBankStatementModal,
		openFinancialReportModal,
		openMapModal,
	} = useModals();

	const selectRejectCategory = useSelectRejectCategory();
	const submitCCDHApprovalSurveyAnalysisMutation = useMutation({
		mutationFn: submitCCDHApprovalSurveyAnalysis,
	});

	const handleSubmit = async (
		values: SubmitCCDHApprovalSurveyAnalysisPayload,
	) => {
		try {
			await submitCCDHApprovalSurveyAnalysisMutation.mutateAsync(values);
			notification.success({
				message: 'Success',
				description: 'Data has been saved',
			});
			navigate('CCDHSurveyApprovalListPage');
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages &&
							message?.metadata?.debugInfo?.messages.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	if (isError) return <GenericErrorMessage />;

	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(mainInformation!.prospectId);
								}}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.industryClass,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Analisa Customer Checking',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerCheckingModal(prospectId);
								}}
							>
								Detail Analisa
							</a>,
						],
						[
							'Dokumen',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
					],
					[
						[
							'SLIK Result',
							mainInformation?.slikResult
								? 'Kol ' + mainInformation?.slikResult
								: '-',
						],
						['Divisi Name', null],
					],
					[
						['FBDH Name', mainInformation?.frm.value],
						['FRM Name', mainInformation?.frm.value],
					],
				]}
				style={{ marginBottom: 20 }}
			/>
			<FleetTitle>Prospect Information</FleetTitle>

			<Typography.Title level={3}>
				Informasi Limit Eksisting Pembiayaan
			</Typography.Title>

			<Table
				scroll={{ x: 'max-content' }}
				dataSource={
					prospectInformation?.existingFinancingLimitInformation?.map(
						(record, key) => {
							return { ...record, key };
						},
					) ?? []
				}
				columns={[
					{
						title: 'No',
						dataIndex: ['no'],
					},
					{
						title: 'No. MKL',
						dataIndex: ['napNo'],
					},
					{
						title: 'Limit Pembiayaan Existing',
						render: (_, record) =>
							formatCurrency(record.existingFinancingLimit),
					},
					{
						title: 'Jenis Limit Pembiayaan',
						dataIndex: ['financingLimitType'],
					},
					{
						title: 'Tipe Limit',
						dataIndex: ['financingLimitCharacteristic'],
					},
					{
						title: 'Expired Date',
						render: (_, record) =>
							dayjs(record.expiredDate).format('DD-MM-YYYY'),
					},
					{
						title: 'NTF',
						render: (_, record) => formatCurrency(record.ntf) || 0,
					},
					{
						title: 'OS Principal',
						render: (_, record) => formatCurrency(record.outstandingPrincipal),
					},
					{
						title: 'Sisa Limit',
						render: (_, record) =>
							formatCurrency(record.outstandingFinancingLimit),
					},
					{
						title: 'Status',
						dataIndex: ['status'],
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>

			<Divider />
			<Typography.Title level={3}>
				Informasi Limit Pengajuan Pembiayaan
			</Typography.Title>

			<Table
				scroll={{ x: 'max-content' }}
				dataSource={
					prospectInformation?.financingApplicationLimitInformation?.map(
						(record, key) => {
							return { ...record, key };
						},
					) ?? []
				}
				columns={[
					{
						title: 'No',
						dataIndex: ['no'],
					},
					{
						title: 'Limit Pembiayaan Existing',
						render: (_, record) =>
							formatCurrency(record.existingFinancingLimit),
					},
					{
						title: 'Jenis Limit Pembiayaan',
						dataIndex: ['financingLimitType'],
					},
					{
						title: 'OS Principal',
						render: (_, record) => formatCurrency(record.outstandingPrincipal),
					},
					{
						title: 'Tujuan Pembiayaan',
						dataIndex: ['financingPurpose'],
					},
					{
						title: 'Pengajuan Limit',
						render: (_, record) => formatCurrency(record.limitApplication),
					},
					{
						title: 'Total Pengajuan Plafon',
						render: (_, record) => formatCurrency(record.totalLimitApplication),
					},
					{
						title: 'Segmen Market',
						dataIndex: ['segmentMarket'],
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>
			<Divider />

			<FleetTitle>Verifikasi Data Pra-Survey</FleetTitle>
			<Typography.Title level={3}>Data Legalitas</Typography.Title>
			<Table
				scroll={{ x: 'max-content' }}
				dataSource={
					customerLegalDocuments?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'View Detail',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal(record.file as FleetFile);
									}}
								>
									<FileSearchOutlined />
								</a>
							);
						},
					},
					{
						title: 'Jenis Dokumen',
						dataIndex: ['documentType'],
					},
					{
						title: 'No Dokumen',
						dataIndex: ['documentNo'],
					},
					{
						title: 'Tanggal Dokumen',
						dataIndex: ['documentDate'],
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>
			<Divider />

			<Typography.Title level={3}>Data Perizinan Usaha</Typography.Title>
			<Table
				scroll={{ x: 'max-content' }}
				dataSource={
					customerBusinessDocuments?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'View Detail',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!record.file?.id) return;
										openPreviewModal({
											id: record.file.id as unknown as number,
											name: record.file.name,
											url: record.file.url,
										});
									}}
								>
									<FileSearchOutlined />
								</a>
							);
						},
					},
					{
						title: 'Jenis Dokumen',
						dataIndex: ['documentType'],
					},
					{
						title: 'No Dokumen',
						dataIndex: ['documentNo'],
					},
					{
						title: 'Penerbit',
						dataIndex: ['publisher', 'value'],
					},
					{
						title: 'Tanggal Dokumen',
						dataIndex: ['documentDate'],
					},
					{
						title: 'Masa Berlaku',
						dataIndex: ['validityPeriod', 'value'],
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>
			<Divider />

			{customerReferralDocuments && customerReferralDocuments.length > 0 && (
				<>
					<Typography.Title level={3}>Data Referral JF</Typography.Title>
					<Table
						scroll={{ x: 'max-content' }}
						dataSource={
							customerReferralDocuments?.map((record, key) => {
								return { ...record, key };
							}) ?? []
						}
						columns={[
							{
								title: 'View Detail',
								align: 'center',
								render: (_, record) => {
									return (
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault();
												openPreviewModal(record.file as FleetFile);
											}}
										>
											<FileSearchOutlined />
										</a>
									);
								},
							},
							{
								title: 'Jenis Dokumen',
								dataIndex: ['documentType'],
							},
							{
								title: 'No Dokumen',
								dataIndex: ['documentNo'],
							},

							{
								title: 'Tanggal Dokumen',
								dataIndex: ['documentDate'],
							},
						]}
						bordered
						pagination={false}
						style={{ marginBottom: 20 }}
					/>
					<Divider />
				</>
			)}

			<Typography.Title level={3}>Data Kontrak Kerja</Typography.Title>
			<Table
				scroll={{ x: 'max-content' }}
				dataSource={
					employmentContracts?.map((record, key) => {
						return {
							...record,
							key,
						};
					}) ?? []
				}
				columns={[
					{
						title: 'View Kontrak Kerja',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openEmploymentContractModal(record);
									}}
								>
									<FileSearchOutlined />
								</a>
							);
						},
					},
					{
						title: 'Bouwheer',
						dataIndex: ['bouwheer', 'name'],
					},
					{
						title: 'Bouwheer ID',
						dataIndex: ['bouwheer', 'id'],
					},
					{
						title: 'Kelas Industri',
						dataIndex: ['bouwheer', 'industrySector', 'industryClass'],
					},
					{
						title: 'Nama Pekerjaan',
						dataIndex: ['jobTitle'],
					},
					{
						title: 'No. SPK',
						dataIndex: ['spkNumber'],
					},
					{
						title: 'Lokasi Pekerjaan',
						dataIndex: ['jobLocation'],
					},
					{
						title: 'Detail Kontrak',
						dataIndex: ['contractDetail'],
					},
					{
						title: 'Nilai Kontrak',
						render: (_, record) => formatCurrency(record.contractValue),
					},
					{
						title: 'Tenor',
						dataIndex: ['tenor'],
						render: (_, record) => record.tenor ?? '-',
					},
					{
						title: 'Sistem Pembayaran',
						dataIndex: ['paymentMethod', 'value'],
						render: (_, record) => record.paymentMethod?.value ?? '-',
					},
					{
						title: 'TOP Pembayaran',
						dataIndex: ['termOfPayment'],
						render: (_, record) => record.termOfPayment ?? '-',
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>
			<Divider />

			<Typography.Title level={3}>Data Rekening Bank</Typography.Title>
			<Table
				scroll={{ x: 'max-content' }}
				dataSource={
					prospectBankStatements?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'View Detail',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openBankStatementModal(record);
									}}
								>
									<FileSearchOutlined />
								</a>
							);
						},
					},
					{
						title: 'Jenis Rekening	',
						dataIndex: ['accountType', 'value'],
					},
					{
						title: 'Nama Bank',
						dataIndex: ['bankName', 'value'],
					},
					{
						title: 'Account Number',
						dataIndex: ['accountNo'],
					},
					{
						title: 'Periode',
						render: (_, record) =>
							dayjs(record.bankStatementPeriod).format('MM/YYYY'),
					},
					{
						title: 'Total Debet',
						dataIndex: ['totalDebit'],
					},
					{
						title: 'Total Kredit',
						dataIndex: ['totalCredit'],
					},
					{
						title: 'Average Saldo',
						dataIndex: ['averageBalance'],
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>
			<Divider />

			<Typography.Title level={3}>Financial Information</Typography.Title>
			<Table
				scroll={{ x: 'max-content' }}
				dataSource={
					prospectFinancialReports?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'View Detail',
						align: 'center',
						render: (_, record) => {
							return (
								<Space
									align="center"
									size="middle"
								>
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openFinancialReportModal(record);
										}}
									>
										<FileSearchOutlined />
									</a>
								</Space>
							);
						},
					},
					{
						title: 'Laporan Keuangan',
						dataIndex: ['auditStatus', 'value'],
					},
					{
						title: 'Nama Auditor',
						dataIndex: ['auditorName'],
					},
					{
						title: 'Periode',
						dataIndex: ['periodType', 'value'],
					},
					{
						title: 'Detail Periode',
						dataIndex: ['periodTypeDetail', 'value'],
					},
					{
						title: 'Tahun',
						dataIndex: ['year'],
					},
					{
						title: 'View Dokumen',
						render: (_, record) => {
							return (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal({
											name: record.financialReportFile.name,
											url: record.financialReportFile.url,
											id: record.financialReportFile.id as unknown as number,
										});
									}}
								>
									View Dokumen
								</a>
							);
						},
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>

			{prospectFinancialReportAnalysis &&
				prospectFinancialReportAnalysis.length > 0 && (
					<table className="table-compare">
						<thead>
							<tr>
								<td />
								{prospectFinancialReportAnalysis?.map((report, index) => (
									<td key={index}>{report?.year}</td>
								))}
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>Detail Periode</td>
								{prospectFinancialReportAnalysis?.map((report, index) => (
									<td key={index}>
										<Input
											disabled
											value={report?.periodDetail}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>EAT/Angsuran 1 TH (1X S/D 0.5X)</td>
								{prospectFinancialReportAnalysis?.map((record, index) => (
									<td key={index}>
										<InputNumber
											value={record.eatInstallmentRatio}
											readOnly
											disabled
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>EBIT/Angsuran 1 TH (3X S/D 1X)</td>
								{prospectFinancialReportAnalysis?.map((record, index) => (
									<td key={index}>
										<InputNumber
											value={record.ebitInstallmentRatio}
											readOnly
											disabled
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>Rata-Rata Saldo Mengendap/ Angsuran 1 Bulan</td>
								{prospectFinancialReportAnalysis?.map((record, index) => (
									<td key={index}>
										<InputNumber
											value={record.averageBalanceInstallmentRatio}
											readOnly
											disabled
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>Current Ratio*</td>
								{prospectFinancialReportAnalysis?.map((record, index) => (
									<td key={index}>
										<InputNumber
											value={record.currentRatio}
											readOnly
											disabled
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>Debt to Equity Ratio*</td>
								{prospectFinancialReportAnalysis?.map((record, index) => (
									<td key={index}>
										<InputNumber
											value={record.debtToEquityRatio}
											readOnly
											disabled
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>Average Collection Period (days)*</td>
								{prospectFinancialReportAnalysis?.map((record, index) => (
									<td key={index}>
										<InputNumber
											value={record.averageCollectionPeriod}
											readOnly
											disabled
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>Average Payable Period (days)*</td>
								{prospectFinancialReportAnalysis?.map((record, index) => (
									<td key={index}>
										<InputNumber
											value={record.averagePayablePeriod}
											readOnly
											disabled
										/>
									</td>
								))}
							</tr>
						</tbody>
					</table>
				)}

			<FleetTitle>Survey Information</FleetTitle>
			<Row gutter={[40, 0]}>
				<Col span={12}>
					<Form.Item
						label="Lokasi Survey Kantor"
						labelAlign="left"
						labelCol={{ span: 10 }}
					>
						<div style={{ fontSize: 16, marginTop: 10 }}>
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openMapModal({
										title: 'Lokasi Survey Kantor',
										address: surveyInformation?.officeAddress,
										latitude: surveyInformation?.officeLat,
										longitude: surveyInformation?.officeLong,
										draggable: false,
										viewOnly: true,
									});
								}}
							>
								{surveyInformation?.officeAddress}
							</a>
						</div>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Tanggal Survey Kantor"
						labelAlign="left"
						labelCol={{ span: 10 }}
					>
						<div style={{ fontSize: 16, marginTop: 10 }}>
							{dayjs(surveyInformation?.surveyOfficeDate).format('DD-MM-YYYY')}
						</div>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 0]}>
				<Col span={12}>
					<Form.Item
						label="Lokasi Survey Site"
						labelAlign="left"
						labelCol={{ span: 10 }}
					>
						<div style={{ fontSize: 16, marginTop: 10 }}>
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openMapModal({
										title: 'Lokasi Survey Site',
										address: surveyInformation?.siteAddress,
										latitude: surveyInformation?.siteLat,
										longitude: surveyInformation?.siteLong,
										draggable: false,
										viewOnly: true,
									});
								}}
							>
								{surveyInformation?.siteAddress}
							</a>
						</div>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Tanggal Survey Site"
						labelAlign="left"
						labelCol={{ span: 10 }}
					>
						<div style={{ fontSize: 16, marginTop: 10 }}>
							{dayjs(surveyInformation?.surveySiteDate).format('DD-MM-YYYY')}
						</div>
					</Form.Item>
				</Col>
			</Row>

			<Divider />
			<Table
				dataSource={
					surveyParticipants?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						render: (_, record) => record.key + 1,
					},
					{
						title: 'Nama Peserta Survey',
						dataIndex: ['name'],
					},
					{
						title: 'Jabatan Peserta Survey',
						dataIndex: ['position'],
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>

			<FleetTitle>Approval History</FleetTitle>
			<Table
				dataSource={
					approvalHistories?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						width: 1,
						align: 'center',
						dataIndex: 'no',
					},
					{
						title: 'Name',
						dataIndex: 'approvalBy',
					},
					{
						title: 'Date',
						dataIndex: 'approvalDate',
					},
					{
						title: 'Process',
						dataIndex: 'approvalProcess',
					},
					{
						title: 'Status',
						align: 'center',
						render: (_, record) => {
							return (
								<span
									className={`review-status ${record.approvalStatus.toLowerCase()}`}
								>
									{record.approvalStatus}
								</span>
							);
						},
					},
					{
						title: 'Approval Notes',
						dataIndex: 'approvalNote',
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName('prospectId')}
					initialValue={mainInformation?.prospectId}
					noStyle
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('approvalStatus')}
							label="Approval Status"
							rules={[rule.required()]}
						>
							<Select
								allowClear={true}
								options={[
									{ value: 'Approve' },
									{ value: 'Return' },
									{ value: 'Reject' },
								]}
							/>
						</Form.Item>
					</Col>
					{value?.approvalStatus === 'Reject' && (
						<Col span={12}>
							<Form.Item
								name={fieldName('rejectCategoryId')}
								label="Category Reject"
								rules={[rule.required()]}
							>
								<Select {...selectRejectCategory} />
							</Form.Item>
						</Col>
					)}
				</Row>
				<Divider />
				<Form.Item
					name={fieldName('approvalNotes')}
					label="Approval Notes"
					labelCol={{ span: 6 }}
					wrapperCol={{
						style: {
							color: 'blue',
							marginLeft: -10,
						},
					}}
					rules={[rule.required()]}
				>
					<TextArea rows={3} />
				</Form.Item>
				<Button
					loading={submitCCDHApprovalSurveyAnalysisMutation.isPending}
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
				>
					SAVE
				</Button>
			</Form>
		</Fragment>
	);
};

export default CCDHASurveyApprovalPage;
